import * as THREE from "three";
import {
  SvgType,
  SymbolType,
  isSvgType,
} from "../../../../types/floorplannerTypes";
import type { Symbol3DRotation } from "../../interaction/symbol3DRotationGizmo";
import {
  Symbol3DAssetTransform,
  getSymbol3DAssetScaleComponents,
} from "./symbol3DAssetTransform";

/** Viewport size used when generating the outline; only the ratio matters. */
const OUTLINE_VIEWBOX_LONG_SIDE = 100;
const OUTLINE_STROKE_WIDTH = 2;
const MIN_FOOTPRINT_SIZE = 1e-4;
const FOOTPRINT_POINT_PRECISION = 1e-6;
const GENERATED_FOOTPRINT_ATTRIBUTE =
  'data-floorplanner-generated-footprint="true"';
const RAW_UNIT_CORRUPTION_MIN_SIZE = 25;
const RAW_UNIT_CORRUPTION_MIN_RATIO = 25;

export type SymbolPlanFootprint = {
  width: number;
  depth: number;
};

export type SymbolAssetFootprintSvg = SymbolPlanFootprint & {
  svgPath: string;
};

/**
 * Plan (top-down) extents of an oriented model bounding box. `orientedBounds`
 * must already have the 3D rotation applied and the plan yaw removed, so the
 * result is expressed in the symbol's own frame - exactly what svgLength and
 * svgHeight describe.
 */
export function getSymbolPlanFootprint(
  orientedBounds: THREE.Box3,
  assetScale: Symbol3DAssetTransform["scale"] = 1,
): SymbolPlanFootprint | null {
  const size = orientedBounds.getSize(new THREE.Vector3());
  const [scaleX, scaleY] = getSymbol3DAssetScaleComponents(assetScale);
  const width = size.x * Math.abs(scaleX);
  const depth = size.y * Math.abs(scaleY);
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(depth) ||
    !(width > MIN_FOOTPRINT_SIZE) ||
    !(depth > MIN_FOOTPRINT_SIZE)
  ) {
    return null;
  }
  return { width, depth };
}

/**
 * Builds a plan outline from the uploaded model's actual mesh vertices after
 * its 3D rotation and render scale have been applied. A convex hull keeps this
 * inexpensive enough to run when rotation finishes while retaining curved and
 * non-rectangular outer profiles (for example, round tables).
 */
export function createSymbolAssetFootprintSvg(
  assetRoot: THREE.Object3D,
  rotation3D: Symbol3DRotation,
  assetScale: Symbol3DAssetTransform["scale"] = 1,
): SymbolAssetFootprintSvg | null {
  assetRoot.updateWorldMatrix(true, true);
  const inverseRootWorld = assetRoot.matrixWorld.clone().invert();
  const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
    new THREE.Euler(
      rotation3D?.pitch ?? 0,
      rotation3D?.roll ?? 0,
      rotation3D?.yaw ?? 0,
      "XYZ",
    ),
  );
  const [scaleX, scaleY] = getSymbol3DAssetScaleComponents(assetScale);
  const point = new THREE.Vector3();
  const instanceMatrix = new THREE.Matrix4();
  const meshInRootMatrix = new THREE.Matrix4();
  const vertexMatrix = new THREE.Matrix4();
  const uniquePoints = new Map<string, THREE.Vector2>();

  assetRoot.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const position = object.geometry?.getAttribute("position");
    if (!position) return;

    meshInRootMatrix.multiplyMatrices(inverseRootWorld, object.matrixWorld);
    const instanceCount =
      object instanceof THREE.InstancedMesh ? object.count : 1;

    for (let instanceIndex = 0; instanceIndex < instanceCount; instanceIndex += 1) {
      if (object instanceof THREE.InstancedMesh) {
        object.getMatrixAt(instanceIndex, instanceMatrix);
        vertexMatrix.multiplyMatrices(meshInRootMatrix, instanceMatrix);
      } else {
        vertexMatrix.copy(meshInRootMatrix);
      }

      for (let vertexIndex = 0; vertexIndex < position.count; vertexIndex += 1) {
        object.getVertexPosition(vertexIndex, point);
        point.applyMatrix4(vertexMatrix).applyMatrix4(rotationMatrix);
        const x = point.x * scaleX;
        const y = point.y * scaleY;
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

        const key = `${Math.round(x / FOOTPRINT_POINT_PRECISION)}:${Math.round(
          y / FOOTPRINT_POINT_PRECISION,
        )}`;
        if (!uniquePoints.has(key)) uniquePoints.set(key, new THREE.Vector2(x, y));
      }
    }
  });

  const hull = convexHull(Array.from(uniquePoints.values()));
  if (hull.length < 3) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  hull.forEach(({ x, y }) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });
  const width = maxX - minX;
  const depth = maxY - minY;
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(depth) ||
    !(width > MIN_FOOTPRINT_SIZE) ||
    !(depth > MIN_FOOTPRINT_SIZE)
  ) {
    return null;
  }

  return {
    width,
    depth,
    svgPath: createSymbolFootprintHullSvg(hull, { minX, maxY, width, depth }),
  };
}

/**
 * A rectangular plan outline matching the given footprint. Uploaded models ship
 * with a footprint silhouette baked at their original orientation; once the
 * model is rotated upright in 3D that silhouette is a side view and no longer
 * describes the plan, so it is replaced with the rotated bounding rectangle.
 */
export function createSymbolFootprintOutlineSvg(
  footprint: SymbolPlanFootprint,
): string {
  const longest = Math.max(footprint.width, footprint.depth);
  const scale = OUTLINE_VIEWBOX_LONG_SIDE / longest;
  const viewWidth = Math.max(footprint.width * scale, 1);
  const viewHeight = Math.max(footprint.depth * scale, 1);
  const inset = OUTLINE_STROKE_WIDTH / 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" ${GENERATED_FOOTPRINT_ATTRIBUTE} viewBox="0 0 ${round(viewWidth)} ${round(viewHeight)}">` +
    `<rect x="${inset}" y="${inset}" ` +
    `width="${round(viewWidth - OUTLINE_STROKE_WIDTH)}" ` +
    `height="${round(viewHeight - OUTLINE_STROKE_WIDTH)}" ` +
    `fill="#ffffff" stroke="#000000" stroke-width="${OUTLINE_STROKE_WIDTH}"/>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function createSymbolFootprintHullSvg(
  hull: THREE.Vector2[],
  bounds: {
    minX: number;
    maxY: number;
    width: number;
    depth: number;
  },
): string {
  const contentLongSide = OUTLINE_VIEWBOX_LONG_SIDE - OUTLINE_STROKE_WIDTH;
  const scale = contentLongSide / Math.max(bounds.width, bounds.depth);
  const inset = OUTLINE_STROKE_WIDTH / 2;
  const viewWidth = Math.max(bounds.width * scale + OUTLINE_STROKE_WIDTH, 1);
  const viewHeight = Math.max(bounds.depth * scale + OUTLINE_STROKE_WIDTH, 1);
  const path = hull
    .map((point, index) => {
      const x = inset + (point.x - bounds.minX) * scale;
      const y = inset + (bounds.maxY - point.y) * scale;
      return `${index === 0 ? "M" : "L"} ${round(x)} ${round(y)}`;
    })
    .join(" ");
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" ${GENERATED_FOOTPRINT_ATTRIBUTE} viewBox="0 0 ${round(viewWidth)} ${round(viewHeight)}">` +
    `<path d="${path} Z" fill="#ffffff" stroke="#000000" ` +
    `stroke-width="${OUTLINE_STROKE_WIDTH}" stroke-linejoin="round"/>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function convexHull(points: THREE.Vector2[]): THREE.Vector2[] {
  if (points.length <= 1) return points;

  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const lower: THREE.Vector2[] = [];
  sorted.forEach((point) => {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0
    ) {
      lower.pop();
    }
    lower.push(point);
  });

  const upper: THREE.Vector2[] = [];
  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    const point = sorted[index];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0
    ) {
      upper.pop();
    }
    upper.push(point);
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function cross(
  origin: THREE.Vector2,
  a: THREE.Vector2,
  b: THREE.Vector2,
): number {
  return (
    (a.x - origin.x) * (b.y - origin.y) -
    (a.y - origin.y) * (b.x - origin.x)
  );
}

export function isUploadedModel3DSymbol(symbol: SymbolType): boolean {
  return (
    isSvgType(symbol) &&
    (symbol.libraryKey === "USER_MODEL_3D" ||
      Boolean(symbol.threeD?.userModel?.modelId))
  );
}

/**
 * Whether the 3D rotation tips the model out of its authored plan orientation.
 * Pure yaw keeps the plan silhouette valid, so only pitch/roll matter.
 */
export function tiltsModelOutOfPlan(rotation3D?: Symbol3DRotation): boolean {
  const epsilon = 1e-6;
  return (
    Math.abs(rotation3D?.pitch ?? 0) > epsilon ||
    Math.abs(rotation3D?.roll ?? 0) > epsilon
  );
}

/**
 * Repairs footprints persisted by the first rotated-footprint implementation,
 * which wrote raw GLB units (commonly millimetres) into metre-based SVG fields.
 * The generated SVG signature and large size mismatch keep this migration from
 * changing intentionally oversized or unrelated SVG symbols.
 */
export function repairUploadedModelFootprintUnitCorruption(
  symbol: SvgType,
): SvgType {
  const intendedWidth = positiveDimension(
    symbol.threeD?.userModel?.dimensions?.width,
  );
  const intendedDepth = positiveDimension(
    symbol.threeD?.userModel?.dimensions?.depth,
  );
  const currentWidth = positiveDimension(symbol.svgLength);
  const currentDepth = positiveDimension(symbol.svgHeight);
  if (
    !isUploadedModel3DSymbol(symbol) ||
    !tiltsModelOutOfPlan(symbol.threeD?.rotation3D) ||
    !isGeneratedFootprintSvgPath(symbol.svgPath) ||
    !intendedWidth ||
    !intendedDepth ||
    !currentWidth ||
    !currentDepth
  ) {
    return symbol;
  }

  const intendedLongSide = Math.max(intendedWidth, intendedDepth);
  const currentLongSide = Math.max(currentWidth, currentDepth);
  if (
    currentLongSide < RAW_UNIT_CORRUPTION_MIN_SIZE ||
    currentLongSide / intendedLongSide < RAW_UNIT_CORRUPTION_MIN_RATIO
  ) {
    return symbol;
  }

  const footprint = { width: intendedWidth, depth: intendedDepth };
  return {
    ...symbol,
    svgPath: createSymbolFootprintOutlineSvg(footprint),
    svgLength: footprint.width,
    svgHeight: footprint.depth,
    svgRatio: footprint.width / footprint.depth,
  };
}

function isGeneratedFootprintSvgPath(svgPath: string): boolean {
  if (!svgPath.startsWith("data:image/svg+xml")) return false;

  let decodedPath = svgPath;
  try {
    decodedPath = decodeURIComponent(svgPath);
  } catch {
    // Keep the original value and check it as-is when legacy data is malformed.
  }

  return (
    decodedPath.includes(GENERATED_FOOTPRINT_ATTRIBUTE) ||
    (decodedPath.includes('<svg xmlns="http://www.w3.org/2000/svg" viewBox=') &&
      decodedPath.includes('<rect x="1" y="1"') &&
      decodedPath.includes(
        'fill="#ffffff" stroke="#000000" stroke-width="2"',
      ))
  );
}

function positiveDimension(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
